import { useProjectStore } from '@/stores';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { Flag, Pencil, Car } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';
import { NodeEditDialog } from './NodeEditDialog';
import type { MilestoneItem } from '@/types';

const defaultMilestones: MilestoneItem[] = [
  { id: '1', sequence: 1, name: 'VPI', description: 'Vehicle Program Initiation', required: false, functionalNode: 'N/A' },
  { id: '2', sequence: 2, name: 'SF', description: 'Styling Freeze', required: false, functionalNode: 'N/A' },
  { id: '3', sequence: 3, name: 'VDR', description: 'Verified Data Release', required: true, functionalNode: 'N/A' },
  { id: '4', sequence: 4, name: 'MC1', description: 'Matching 1', required: false, functionalNode: 'N/A' },
  { id: '5', sequence: 5, name: 'APPV', description: 'Advanced Process Validation Vehicle', required: true, functionalNode: '首次造车' },
  { id: '6', sequence: 6, name: 'MC2', description: 'Matching 2', required: false, functionalNode: 'N/A' },
  { id: '7', sequence: 7, name: 'PPV', description: 'Process Validation Vehicle', required: false, functionalNode: 'N/A' },
  { id: '8', sequence: 8, name: 'VTC', description: 'Vehicle Testing Complete', required: false, functionalNode: 'N/A' },
  { id: '9', sequence: 9, name: 'VDC', description: 'Validation Documentation Complete', required: false, functionalNode: 'N/A' },
  { id: '10', sequence: 10, name: 'S', description: 'Salable', required: true, functionalNode: '可销售车' },
  { id: '11', sequence: 11, name: 'SORP', description: 'Start Of Regular Production', required: true, functionalNode: 'N/A' },
];

interface NodeConfig {
  key: string;
  label: string;
  order: number;
}

const SIZES = {
  infoHeight: 32,
  circleSize: 40,
  rowGap: 20,
};

interface LineSegment {
  type: 'horizontal' | 'vertical';
  top: number;
  left: number;
  width?: number;
  height?: number;
}

export function VehicleNodesRoadmap() {
  const { currentProject } = useProjectStore();
  const nodes = currentProject?.nodes || {};
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [lineSegments, setLineSegments] = useState<LineSegment[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const milestones = currentProject?.vehicleMilestoneTemplate?.milestones || defaultMilestones;
  
  // 按 sequence 排序后再过滤，确保显示顺序正确
  const sortedMilestones = [...milestones].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  
  // 过滤掉无效的里程碑：name 为空或为 N/A
  const validMilestones = sortedMilestones.filter(m => {
    const name = m.name?.trim().toUpperCase() || '';
    const isValid = m && m.name && name !== '' && name !== 'N/A';
    return isValid;
  });

  const nodeConfigs: NodeConfig[] = validMilestones.map((m, index) => ({
    key: m.name,
    label: m.name,
    order: index + 1,
  }));

  const displayNodes = nodeConfigs.map(config => ({
    ...config,
    date: nodes[config.key as keyof typeof nodes],
  }));

  useEffect(() => {
    const calculateLines = () => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const getCenter = (index: number) => {
        const nodeEl = nodeRefs.current[index];
        if (!nodeEl) return null;
        const rect = nodeEl.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2 - containerRect.left,
          y: rect.top + rect.height / 2 - containerRect.top,
        };
      };

      const totalNodes = displayNodes.length;
      if (totalNodes <= 1) {
        setLineSegments([]);
        return;
      }

      const segments: LineSegment[] = [];
      const rowSize = 4;
      const totalRows = Math.ceil(totalNodes / rowSize);
      
      const rowIndices: number[][] = [];
      for (let row = 0; row < totalRows; row++) {
        const start = row * rowSize;
        const end = Math.min(start + rowSize, totalNodes);
        const rowNodes = [];
        for (let i = start; i < end; i++) {
          rowNodes.push(i);
        }
        rowIndices.push(rowNodes);
      }

      for (let rowIdx = 0; rowIdx < rowIndices.length; rowIdx++) {
        const row = rowIndices[rowIdx];
        const isEvenRow = rowIdx % 2 === 0;
        
        const sortedRow = isEvenRow ? row : [...row].reverse();
        
        for (let i = 0; i < sortedRow.length - 1; i++) {
          const c1 = getCenter(sortedRow[i]);
          const c2 = getCenter(sortedRow[i + 1]);
          
          if (!c1 || !c2) continue;
          
          segments.push({
            type: 'horizontal',
            top: c1.y,
            left: Math.min(c1.x, c2.x),
            width: Math.abs(c2.x - c1.x),
          });
        }

        if (rowIdx < rowIndices.length - 1) {
          const nextRow = rowIndices[rowIdx + 1];
          const isNextEven = (rowIdx + 1) % 2 === 0;
          
          // 蛇形连线逻辑：
          // 偶数行（从左到右显示）：结束于右边缘，需要连接到下一行的右边缘
          // 奇数行（从右到左显示）：结束于左边缘，需要连接到下一行的左边缘
          
          // 当前行的最后一个节点（视觉上最靠近边缘的节点）：
          // 偶数行：数组最后一个元素（显示在最右边）
          // 奇数行：数组最后一个元素（因为显示反转，所以显示在最左边）
          const lastInCurrentRow = row[row.length - 1];
          
          // 下一行需要连接的节点（视觉上最靠近边缘的节点）：
          // 下一行是偶数行：数组第一个元素（显示在最左边）
          // 下一行是奇数行：数组第一个元素（因为显示反转，所以显示在最右边）
          const firstInNextRow = nextRow[0];
          
          const c1 = getCenter(lastInCurrentRow);
          const c2 = getCenter(firstInNextRow);
          
          if (!c1 || !c2) continue;

          // 边缘位置：偶数行用右边缘，奇数行用左边缘
          const edgeX = containerRect.width * (isEvenRow ? 0.96 : 0.04);
          
          if (isEvenRow) {
            // 从当前行最右边 → 右边缘
            segments.push({
              type: 'horizontal',
              top: c1.y,
              left: c1.x,
              width: edgeX - c1.x,
            });
            
            // 垂直线
            segments.push({
              type: 'vertical',
              top: c1.y,
              left: edgeX,
              height: c2.y - c1.y,
            });
            
            // 从右边缘 → 下一行最右边
            segments.push({
              type: 'horizontal',
              top: c2.y,
              left: c2.x,
              width: edgeX - c2.x,
            });
          } else {
            // 从当前行最左边 → 左边缘
            segments.push({
              type: 'horizontal',
              top: c1.y,
              left: edgeX,
              width: c1.x - edgeX,
            });
            
            // 垂直线
            segments.push({
              type: 'vertical',
              top: c1.y,
              left: edgeX,
              height: c2.y - c1.y,
            });
            
            // 从左边缘 → 下一行最左边
            segments.push({
              type: 'horizontal',
              top: c2.y,
              left: edgeX,
              width: c2.x - edgeX,
            });
          }
        }
      }

      setLineSegments(segments);
    };

    calculateLines();
    window.addEventListener('resize', calculateLines);
    return () => window.removeEventListener('resize', calculateLines);
  }, [displayNodes.length]);

  const getNodeIcon = (globalIndex: number, total: number) => {
    const isLast = globalIndex === total - 1;
    return (
      <div 
        ref={el => { nodeRefs.current[globalIndex] = el; }}
        className={`rounded-full flex items-center justify-center relative z-10 ${
          isLast 
            ? 'bg-gray-800 border-2 border-gray-600' 
            : 'bg-blue-600 text-white text-xs font-bold'
        }`}
        style={{ 
          width: `${SIZES.circleSize}px`, 
          height: `${SIZES.circleSize}px` 
        }}
      >
        {isLast ? <Flag className="w-5 h-5 text-white" /> : globalIndex + 1}
      </div>
    );
  };

  const getNodeDate = (date: string | undefined) => {
    return date || 'TBD';
  };

  const rowSize = 4;
  const totalNodes = displayNodes.length;
  const totalRows = Math.ceil(totalNodes / rowSize);
  
  const rowDisplayOrder: number[][] = [];
  for (let row = 0; row < totalRows; row++) {
    const start = row * rowSize;
    const end = Math.min(start + rowSize, totalNodes);
    const rowNodes = [];
    for (let i = start; i < end; i++) {
      rowNodes.push(i);
    }
    if (row % 2 === 1) {
      rowNodes.reverse();
    }
    rowDisplayOrder.push(rowNodes);
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Car className="w-6 h-6 text-gray-500" />
            Vehicle Milestones
          </CardTitle>
          <button
            onClick={() => setIsEditDialogOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Edit milestones"
          >
            <Pencil className="w-4 h-4 text-gray-600" />
          </button>
        </CardHeader>
        <CardContent>
          <div ref={containerRef} className="relative" style={{ height: `${totalRows * 96 + 8}px` }}>
            <div className="absolute inset-0" style={{ zIndex: 0 }}>
              {lineSegments.map((segment, index) => (
                segment.type === 'horizontal' ? (
                  <div 
                    key={index}
                    className="absolute h-0.5 bg-gray-300" 
                    style={{ 
                      top: `${segment.top}px`, 
                      left: `${segment.left}px`, 
                      width: `${segment.width}px` 
                    }} 
                  />
                ) : (
                  <div 
                    key={index}
                    className="absolute w-0.5 bg-gray-300" 
                    style={{ 
                      top: `${segment.top}px`, 
                      left: `${segment.left}px`, 
                      height: `${segment.height}px` 
                    }} 
                  />
                )
              ))}
            </div>
            
            <div className="relative" style={{ zIndex: 1 }}>
              {rowDisplayOrder.map((rowIndices, rowIndex) => (
                <div 
                  key={rowIndex} 
                  className="relative last:mb-0"
                  style={{ marginBottom: `${SIZES.rowGap}px` }}
                >
                  <div className="grid grid-cols-4 gap-1">
                    {rowIndices.map((nodeIndex) => {
                      const node = displayNodes[nodeIndex];
                      const date = getNodeDate(node.date);
                      
                      return (
                        <div 
                          key={node.key} 
                          className="flex flex-col items-center text-center"
                        >
                          <div 
                            className="mb-1"
                            style={{ height: `${SIZES.infoHeight}px` }}
                          >
                            <div className="flex items-center justify-center gap-0.5">
                              <span className="font-semibold text-gray-800 text-xs">{node.label}</span>
                            </div>
                            <div className="text-xs text-gray-400">
                              {date}
                            </div>
                          </div>
                          
                          {getNodeIcon(nodeIndex, displayNodes.length)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <NodeEditDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        nodes={displayNodes}
      />
    </>
  );
}
